import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import ApplyPresetFooter from './ApplyPresetFooter';
import { Button } from 'foundations-components/transitional/components/Button';
import * as PresetActions from '../../actions/PresetActions';
import { getIgnoreUserThemeOverrides, getSelectedThemePreset } from '../../selectors/presetsSelectors';
import { getHasBrandedThemePresetsAccessScope } from '../../selectors/authSelectors';
import { Flex } from 'foundations-components/components/Flex';
import { Breadcrumbs } from 'foundations-components/transitional/components/Breadcrumbs';
import { Link } from 'foundations-components/transitional/components/Link';
import { View } from 'foundations-components/transitional/components/View';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import PresetTile from './PresetTile';
import { H7 } from 'foundations-components/transitional/components/H7';
import { AIBadge } from 'ai-components-ui-library/components/informative/AIBadge';
import enviro from 'enviro';
import { Small } from 'foundations-components/transitional/components/Small';
import { track } from '../../utils/standaloneUsageTracker';
import { EVENTS } from '../../constants/usage/standaloneTracking';
import { saveSettingsBuffer } from '../../actions/ThemeSettingsActions';
import { PRESET_TYPES } from '../../constants/Presets';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const isQa = enviro.isQa();
const i18nKey = key => `theme-previewer.themePresets.${key}`;
const PresetListPanel = ({
  activeThemePresetId,
  hasUserThemeOverrides,
  isUniversalThemeEditing,
  onClose,
  presets,
  reloadIframePreview,
  selectedThemePresetId,
  themePath
}) => {
  const dispatch = useDispatch();
  const [isApplyPresetFooterOpen, setIsApplyPresetFooterOpen] = useState(false);
  const [isApplyingPreset, setIsApplyingPreset] = useState(false);
  const isViewingCurrentStyle = !useSelector(getIgnoreUserThemeOverrides);
  const hasBrandedThemePresetsAccessScope = useSelector(getHasBrandedThemePresetsAccessScope);
  const [brandedPresets, defaultPresets] = useMemo(() => {
    const _brandedPresets = [];
    const _defaultPresets = [];
    const shouldFilterActivePreset = activeThemePresetId && !hasUserThemeOverrides;
    presets.forEach(preset => {
      if (shouldFilterActivePreset && preset.id === activeThemePresetId) {
        return;
      }
      if (preset.type === PRESET_TYPES.CUSTOM) {
        _brandedPresets.push(preset);
      } else {
        _defaultPresets.push(preset);
      }
    });
    return [_brandedPresets, _defaultPresets];
  }, [presets, activeThemePresetId, hasUserThemeOverrides]);
  const showDefaultStylesPreset = Boolean(activeThemePresetId) || hasUserThemeOverrides;
  const showBrandedPresets = brandedPresets.length > 0 && hasBrandedThemePresetsAccessScope;
  const showOtherPresets = defaultPresets.length > 0 || showDefaultStylesPreset;
  const selectPreset = presetId => {
    if (presetId) {
      dispatch(PresetActions.setSelectedPreset(presetId));
    } else {
      dispatch(PresetActions.clearSelectedPreset());
    }
    reloadIframePreview === null || reloadIframePreview === void 0 || reloadIframePreview();
  };
  const restoreActivePreset = () => {
    if (!isApplyingPreset) {
      dispatch(PresetActions.setIgnoreUserThemeOverrides(false));
    }
    reloadIframePreview === null || reloadIframePreview === void 0 || reloadIframePreview();
    onClose();
  };

  // On unmount, restore the active preset
  const restoreActivePresetRef = useRef(restoreActivePreset);
  useEffect(() => {
    restoreActivePresetRef.current = restoreActivePreset;
  });
  useEffect(() => {
    return () => {
      restoreActivePresetRef.current();
    };
  }, []);
  const handleSelectPreset = (presetId, isCurrentStyle = false) => {
    dispatch(PresetActions.setIgnoreUserThemeOverrides(!isCurrentStyle));
    setIsApplyPresetFooterOpen(!isCurrentStyle);
    selectPreset(presetId);
  };
  const selectedThemePreset = useSelector(getSelectedThemePreset);
  const handleApplyPreset = () => {
    setIsApplyingPreset(true);
    dispatch(selectedThemePresetId ? PresetActions.setActivePreset(selectedThemePresetId) : PresetActions.clearActivePreset());
    // In the UTE, setActivePreset will trigger autosave so don't trigger duplicate save here
    if (!isUniversalThemeEditing) {
      dispatch(saveSettingsBuffer({
        fromUniversal: false
      })).catch(e => console.error(e));
    }
    reloadIframePreview === null || reloadIframePreview === void 0 || reloadIframePreview();
    const {
      type,
      name
    } = selectedThemePreset || {};
    track(EVENTS.PRESET_APPLIED, {
      type,
      name
    });
    onClose();
  };
  const handleCancel = () => {
    dispatch(PresetActions.setIgnoreUserThemeOverrides(false));
    if (selectedThemePresetId !== activeThemePresetId) {
      selectPreset(activeThemePresetId);
    } else {
      reloadIframePreview === null || reloadIframePreview === void 0 || reloadIframePreview();
    }
    setIsApplyPresetFooterOpen(false);
  };
  return /*#__PURE__*/_jsxs(Flex, {
    blockSize: "100%",
    flexDirection: "column",
    justifyContent: "space-between",
    children: [/*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      overflowY: "auto",
      padding: "500",
      children: [/*#__PURE__*/_jsx(View, {
        children: /*#__PURE__*/_jsx(Breadcrumbs, {
          flush: true,
          marginBlockStart: "0",
          marginBlockEnd: "500",
          children: /*#__PURE__*/_jsx(Link, {
            onClick: onClose,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('back')
            })
          })
        })
      }), /*#__PURE__*/_jsxs(View, {
        "data-test-id": "current-style-preset",
        marginBlockEnd: "600",
        children: [/*#__PURE__*/_jsx(H7, {
          marginBlockEnd: "300",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('currentStyle')
          })
        }), /*#__PURE__*/_jsx(PresetTile, {
          ignoreUserThemeOverrides: false,
          selected: isViewingCurrentStyle,
          presetId: activeThemePresetId,
          onClick: () => handleSelectPreset(activeThemePresetId, true),
          themePath: themePath
        })]
      }), showBrandedPresets && /*#__PURE__*/_jsxs(View, {
        "data-test-id": "branded-presets-section",
        marginBlockEnd: "600",
        children: [/*#__PURE__*/_jsx(H7, {
          marginBlockEnd: "300",
          children: /*#__PURE__*/_jsxs(Flex, {
            justifyContent: "space-between",
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('brandedPresets')
            }), /*#__PURE__*/_jsx(AIBadge, {})]
          })
        }), /*#__PURE__*/_jsx(List, {
          children: brandedPresets.map((preset, index) => {
            var _preset$extractionId;
            return /*#__PURE__*/_jsxs(ListItem, {
              marginBlockEnd: index === brandedPresets.length - 1 ? '0' : '300',
              children: [/*#__PURE__*/_jsx(PresetTile, {
                selected: selectedThemePresetId === preset.id && !isViewingCurrentStyle,
                onClick: () => handleSelectPreset(preset.id),
                presetId: preset.id,
                themePath: themePath
              }), isQa && /*#__PURE__*/_jsx(Small, {
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: i18nKey('presetsDebugMessage'),
                  options: {
                    presetId: preset.id.toString(),
                    extractionId: ((_preset$extractionId = preset.extractionId) === null || _preset$extractionId === void 0 ? void 0 : _preset$extractionId.toString()) || 'n/a'
                  }
                })
              })]
            }, index);
          })
        })]
      }), showOtherPresets && /*#__PURE__*/_jsxs(View, {
        "data-test-id": "other-presets-section",
        children: [/*#__PURE__*/_jsx(H7, {
          marginBlockEnd: "300",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey(showBrandedPresets ? 'otherPresets' : 'presets')
          })
        }), /*#__PURE__*/_jsxs(List, {
          children: [defaultPresets.map((preset, index) => /*#__PURE__*/_jsx(ListItem, {
            marginBlockEnd: "300",
            children: /*#__PURE__*/_jsx(PresetTile, {
              selected: selectedThemePresetId === preset.id && !isViewingCurrentStyle,
              onClick: () => handleSelectPreset(preset.id),
              presetId: preset.id,
              themePath: themePath
            })
          }, index)), showDefaultStylesPreset && /*#__PURE__*/_jsx(ListItem, {
            marginBlockEnd: "300",
            children: /*#__PURE__*/_jsx(PresetTile, {
              selected: !selectedThemePresetId && !isViewingCurrentStyle,
              onClick: () => handleSelectPreset(),
              themePath: themePath
            })
          })]
        })]
      })]
    }), /*#__PURE__*/_jsxs(ApplyPresetFooter, {
      isOpen: isApplyPresetFooterOpen,
      isUniversalThemeEditing: isUniversalThemeEditing,
      onOpenComplete: () => {
        setIsApplyPresetFooterOpen(true);
      },
      children: [/*#__PURE__*/_jsx(LoadingButton, {
        "data-test-id": "apply-preset-button",
        loading: isApplyingPreset,
        onClick: handleApplyPreset,
        use: "primary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('apply')
        })
      }), /*#__PURE__*/_jsx(Button, {
        onClick: handleCancel,
        use: "secondary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('cancel')
        })
      })]
    })]
  });
};
export default PresetListPanel;